/**
 * AuthService - Centralized authentication service
 * Aligned with API documentation (api_docs/auth_apis.md)
 * Supports three authentication methods:
 * 1. Server Auth (email/password) - POST /auth/login
 * 2. Firebase OAuth (Google via Firebase SDK) - POST /auth/firebase
 * 3. Google OAuth (direct backend flow) - POST /auth/google
 * 
 * Uses helper modules to keep Firebase and Google OAuth logic isolated
 */

import config from '../config/index';
import { firebaseAuthHelper, type FirebaseConfig } from './helpers/firebaseAuthHelper';
import { googleAuthHelper, type GoogleOAuthConfig } from './helpers/googleAuthHelper';
import { TokenManager, UserDataManager } from '../utils/tokenManager';
import { logger } from '../utils/logger';

// ============================================================================
// TYPES - Aligned with API Documentation
// ============================================================================

/**
 * User object as defined in auth_apis.md
 */
export interface User {
  public_id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'moderator';
  verified: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Auth tokens as defined in auth_apis.md
 * Response format: { token: string, refresh_token: string, token_type: "Bearer" }
 */
export interface AuthTokens {
  token: string;
  refresh_token: string;
  token_type: string;
}


/**
 * Auth service configuration
 */
export interface AuthConfig {
  enableServerAuth: boolean;
  enableFirebaseAuth: boolean;
  enableGoogleOAuth: boolean;
  firebase?: FirebaseConfig;
  googleOAuth?: GoogleOAuthConfig;
}

/**
 * API response wrapper as defined in index.md
 */
interface ApiResponse<T> {
  status: 'success' | 'error';
  message: string;
  data: T;
}

// ============================================================================
// AUTH SERVICE CLASS
// ============================================================================

class AuthService {
  private config: AuthConfig;
  private refreshTokenTimeout: NodeJS.Timeout | null = null;

  constructor(config: AuthConfig) {
    this.config = config;
    this.initialize();
  }

  /**
   * Initialize authentication helpers based on configuration
   */
  private initialize(): void {
    // Initialize Firebase if enabled
    if (this.config.enableFirebaseAuth && this.config.firebase) {
      try {
        firebaseAuthHelper.initialize(this.config.firebase);
      } catch (error) {
        logger.child('AUTH').error('Failed to initialize Firebase:', error);
      }
    }

    // Initialize Google OAuth if enabled
    if (this.config.enableGoogleOAuth && this.config.googleOAuth) {
      try {
        googleAuthHelper.initialize(this.config.googleOAuth);
      } catch (error) {
        logger.child('AUTH').error('Failed to initialize Google OAuth:', error);
      }
    }
  }

  // ==========================================================================
  // AUTHENTICATION METHODS - Aligned with auth_apis.md
  // ==========================================================================

  /**
   * Register new user
   * Endpoint: POST /auth/register
   * Documented in auth_apis.md
   */
  async register(data: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Promise<{ user: User; tokens: AuthTokens }> {
    try {
      const response = await fetch(`${config.API.BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const result: ApiResponse<{ user: User; token: string; refresh_token: string; token_type: string }> = await response.json();
      
      const tokens: AuthTokens = {
        token: result.data.token,
        refresh_token: result.data.refresh_token,
        token_type: result.data.token_type,
      };

      // Store tokens and user using TokenManager
      TokenManager.storeTokens({ 
        token: tokens.token, 
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (60 * 60 * 1000) 
      });
      UserDataManager.storeUser(result.data.user);
      this.scheduleTokenRefresh();

      return {
        user: result.data.user,
        tokens,
      };
    } catch (error) {
      logger.child('AUTH').error('Registration failed:', error);
      throw error;
    }
  }

  /**
   * Login with email/password (Server Auth)
   * Endpoint: POST /auth/login
   * Documented in auth_apis.md
   */
  async loginWithCredentials(email: string, password: string): Promise<{ user: User; tokens: AuthTokens }> {
    if (!this.config.enableServerAuth) {
      throw new Error('Server authentication is not enabled');
    }

    try {
      const response = await fetch(`${config.API.BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const result: ApiResponse<{ user: User; token: string; refresh_token: string; token_type: string }> = await response.json();
      
      const tokens: AuthTokens = {
        token: result.data.token,
        refresh_token: result.data.refresh_token,
        token_type: result.data.token_type,
      };

      // Store tokens and user using TokenManager
      TokenManager.storeTokens({ 
        token: tokens.token, 
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (60 * 60 * 1000) 
      });
      UserDataManager.storeUser(result.data.user);
      this.scheduleTokenRefresh();

      return {
        user: result.data.user,
        tokens,
      };
    } catch (error) {
      logger.child('AUTH').error('Credential login failed:', error);
      throw error;
    }
  }

  /**
   * Login with Firebase OAuth (Google)
   * Flow: Firebase SDK → ID Token → Backend verification
   * Endpoint: POST /auth/firebase
   * Documented in auth_apis.md
   */
  async loginWithFirebase(): Promise<{ user: User; tokens: AuthTokens }> {
    if (!this.config.enableFirebaseAuth) {
      throw new Error('Firebase authentication is not enabled');
    }

    if (!firebaseAuthHelper.isInitialized()) {
      throw new Error('Firebase is not initialized');
    }

    try {
      // Step 1: Get Firebase ID token via helper
      const { idToken } = await firebaseAuthHelper.signInWithGoogle();

      // Step 2: Send ID token to backend for verification
      const response = await fetch(`${config.API.BASE_URL}/auth/firebase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Firebase authentication failed');
      }

      const result: ApiResponse<{ user: User; token: string; refresh_token: string; token_type: string }> = await response.json();
      
      const tokens: AuthTokens = {
        token: result.data.token,
        refresh_token: result.data.refresh_token,
        token_type: result.data.token_type,
      };

      // Store tokens and user using TokenManager
      TokenManager.storeTokens({ 
        token: tokens.token, 
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (60 * 60 * 1000) 
      });
      UserDataManager.storeUser(result.data.user);
      this.scheduleTokenRefresh();

      return {
        user: result.data.user,
        tokens,
      };
    } catch (error: any) {
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        // Redirect is happening, this is expected
        throw error;
      }
      logger.child('AUTH').error('Firebase login failed:', error);
      throw error;
    }
  }

  /**
   * Login with Google OAuth (Direct Backend Flow)
   * Flow: Google consent → Auth code → Backend exchange
   * Endpoint: POST /auth/google
   * Documented in auth_apis.md
   */
  async loginWithGoogleOAuth(code: string): Promise<{ user: User; tokens: AuthTokens }> {
    if (!this.config.enableGoogleOAuth) {
      throw new Error('Google OAuth is not enabled');
    }

    try {
      const response = await fetch(`${config.API.BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Google OAuth failed');
      }

      const result: ApiResponse<{ user: User; token: string; refresh_token: string; token_type: string }> = await response.json();
      
      const tokens: AuthTokens = {
        token: result.data.token,
        refresh_token: result.data.refresh_token,
        token_type: result.data.token_type,
      };

      // Store tokens and user using TokenManager
      TokenManager.storeTokens({ 
        token: tokens.token, 
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (60 * 60 * 1000) 
      });
      UserDataManager.storeUser(result.data.user);
      this.scheduleTokenRefresh();

      return {
        user: result.data.user,
        tokens,
      };
    } catch (error) {
      logger.child('AUTH').error('Google OAuth login failed:', error);
      throw error;
    }
  }

  /**
   * Logout user
   * Endpoint: POST /auth/logout
   * Documented in auth_apis.md
   */
  async logout(): Promise<void> {
    try {
      // Clear refresh timeout
      if (this.refreshTokenTimeout) {
        clearTimeout(this.refreshTokenTimeout);
        this.refreshTokenTimeout = null;
      }

      // Logout from Firebase if using Firebase Auth
      if (this.config.enableFirebaseAuth) {
        try {
          await firebaseAuthHelper.signOut();
        } catch (error) {
          logger.child('AUTH').warn('Firebase sign-out failed:', error);
        }
      }

      // Call backend logout endpoint
      const token = TokenManager.getToken();
      if (token) {
        try {
          await fetch(`${config.API.BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          logger.child('AUTH').warn('Backend logout failed:', error);
        }
      }

      // Clear stored data using TokenManager
      TokenManager.clearTokens();
      UserDataManager.clearUser();
    } catch (error) {
      logger.child('AUTH').error('Logout failed:', error);
      // Force clear even if API call fails
      TokenManager.clearTokens();
      UserDataManager.clearUser();
      throw error;
    }
  }

  /**
   * Refresh authentication token using refresh token
   * Endpoint: POST /auth/refresh
   * Documented in auth_apis.md
   */
  async refreshToken(): Promise<AuthTokens> {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${config.API.BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const result: ApiResponse<{ user: User; token: string; refresh_token: string; token_type: string }> = await response.json();
      
      const tokens: AuthTokens = {
        token: result.data.token,
        refresh_token: result.data.refresh_token,
        token_type: result.data.token_type,
      };

      // Store new tokens using TokenManager
      TokenManager.storeTokens({ 
        token: tokens.token, 
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (60 * 60 * 1000) 
      });
      
      // Update user data if returned
      if (result.data.user) {
        UserDataManager.storeUser(result.data.user);
      }
      
      // Schedule next refresh
      this.scheduleTokenRefresh();

      return tokens;
    } catch (error) {
      logger.child('AUTH').error('Token refresh failed:', error);
      // Don't clear tokens immediately - let the interceptor handle logout
      throw error;
    }
  }

  // ==========================================================================
  // TOKEN & USER MANAGEMENT
  // ==========================================================================

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return TokenManager.getToken();
  }

  getRefreshToken(): string | null {
    return TokenManager.getRefreshToken();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return TokenManager.isTokenValid();
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): User | null {
    return UserDataManager.getUser();
  }

  /**
   * Update current user in storage
   */
  updateUser(user: User): void {
    UserDataManager.storeUser(user);
  }

  // ==========================================================================
  // HELPER ACCESS (for UI components)
  // ==========================================================================

  /**
   * Get Google OAuth authorization URL
   * For redirect-based Google OAuth flow
   */
  getGoogleOAuthUrl(state?: string): string {
    if (!this.config.enableGoogleOAuth) {
      throw new Error('Google OAuth is not enabled');
    }
    return googleAuthHelper.getAuthorizationUrl(state);
  }

  /**
   * Redirect to Google OAuth consent screen
   */
  redirectToGoogleOAuth(state?: string): void {
    if (!this.config.enableGoogleOAuth) {
      throw new Error('Google OAuth is not enabled');
    }
    googleAuthHelper.redirectToGoogle(state);
  }

  /**
   * Parse Google OAuth callback URL
   * Call this on your OAuth callback page
   */
  parseGoogleOAuthCallback(url?: string): { code: string; state?: string } | null {
    return googleAuthHelper.parseCallbackUrl(url);
  }

  // ==========================================================================
  // PRIVATE HELPERS
  // ==========================================================================

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }

    const expiresAt = TokenManager.getTokenExpiration();
    if (!expiresAt) return;

    const refreshTime = expiresAt - Date.now() - config.AUTH.TOKEN_REFRESH_BUFFER;
    
    if (refreshTime > 0) {
      this.refreshTokenTimeout = setTimeout(async () => {
        try {
          await this.refreshToken();
        } catch (error) {
          logger.child('AUTH').error('Automatic token refresh failed:', error);
        }
      }, refreshTime);
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<AuthConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.initialize();
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

// Default configuration from centralized config
const defaultConfig: AuthConfig = {
  enableServerAuth: config.AUTH.ENABLE_SERVER_AUTH,
  enableFirebaseAuth: config.AUTH.ENABLE_FIREBASE_AUTH,
  enableGoogleOAuth: config.AUTH.ENABLE_GOOGLE_OAUTH,
  firebase: {
    apiKey: config.FIREBASE.API_KEY,
    authDomain: config.FIREBASE.AUTH_DOMAIN,
    projectId: config.FIREBASE.PROJECT_ID,
    storageBucket: config.FIREBASE.STORAGE_BUCKET,
    messagingSenderId: config.FIREBASE.MESSAGING_SENDER_ID,
    appId: config.FIREBASE.APP_ID,
  },
  googleOAuth: config.AUTH.ENABLE_GOOGLE_OAUTH
    ? {
        clientId: config.GOOGLE_OAUTH.CLIENT_ID,
        redirectUri: config.GOOGLE_OAUTH.REDIRECT_URI,
        scope: config.GOOGLE_OAUTH.SCOPE,
      }
    : undefined,
};

export const authService = new AuthService(defaultConfig);
export default authService;
